import { API_BASE } from "../config";
import type { ApiErrorBody } from "./types";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;
  /** Details some errors carry (`price_changed`: `{price}`). */
  readonly data: unknown;

  constructor(status: number, code: string, message: string, requestId?: string, data?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.data = data;
  }
}

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null | undefined): void {
  csrfToken = token ?? null;
}

function readCsrfCookie(): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(/(?:^|;\s*)vd_csrf=([^;]+)/);
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}

/** The CSRF token a write sends (`x-csrf-token`), for requests made outside `api`. */
export function csrfHeader(): Record<string, string> {
  const token = csrfToken ?? readCsrfCookie();
  return token ? { "x-csrf-token": token } : {};
}

type Query = Record<string, string | number | boolean | null | undefined>;

export function buildQuery(q: Query | undefined): string {
  if (!q) return "";
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === "") continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  query?: Query;
  body?: unknown;
  /** A non-JSON body sent as is (an audio file, a CSV's text), with its content type. */
  raw?: { body: Blob | ArrayBuffer | string; type: string };
  /** A multipart body; the browser sets the content type with its boundary. */
  form?: FormData;
  signal?: AbortSignal;
  /** Extra request headers (an `Idempotency-Key`). */
  headers?: Record<string, string>;
}

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = { accept: "application/json", ...opts.headers };
  if (opts.raw) headers["content-type"] = opts.raw.type;
  else if (opts.form) {
    // No content type: fetch adds multipart/form-data with the boundary.
  } else if (opts.body !== undefined) headers["content-type"] = "application/json";
  if (method !== "GET") {
    const token = csrfToken ?? readCsrfCookie();
    if (token) headers["x-csrf-token"] = token;
  }
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}${buildQuery(opts.query)}`, {
      method,
      headers,
      credentials: "include",
      body:
        opts.form ??
        (opts.raw ? opts.raw.body : opts.body === undefined ? undefined : JSON.stringify(opts.body)),
      signal: opts.signal,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    throw new ApiError(0, "network_error", "Can't reach the server. Check your connection and try again.");
  }
  const requestId = res.headers.get("x-request-id") ?? undefined;
  if (!res.ok) {
    let code = "http_error";
    let message = `The server returned ${res.status}.`;
    let data: unknown;
    try {
      const body = (await res.json()) as ApiErrorBody;
      if (body?.error) {
        code = body.error.code;
        message = body.error.message;
        data = body.error.data;
      }
    } catch {
      // Body was not JSON; keep the generic message.
    }
    if (res.status === 401 && onUnauthorized) onUnauthorized();
    throw new ApiError(res.status, code, message, requestId, data);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Something went wrong.";
}

/** A binary GET with the session cookie (a member's photo). */
export async function apiBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: "include", signal });
  if (!res.ok) throw new ApiError(res.status, "http_error", `The server returned ${res.status}.`);
  return res.blob();
}
